import { useEffect, useMemo, useState } from "react"
import {
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  Clock3,
  MapPin,
  Star,
} from "lucide-react"
import { useLocation, useNavigate } from "react-router-dom"
import bookingService from "../../../api/services/bookingService"
import { useToast } from "../../../hooks/useToast"
import { getBookedDayCounts, toDayKey } from "../../../utils/bookedDays"
import { hasTripStarted } from "../../../utils/tripStatus"
import {
  getAssistantBookingEarnings,
  getBookingGrossAmount,
} from "../../../utils/bookingEarnings"
import {
  resolveApprovalReason,
  resolveApprovalState,
} from "./approvalState"
import { useProfileStore } from "../../../hooks/useProfileStore"
import { useAuthStore } from "../../../hooks/useAuthStore"
import { useUnreadMessagesCountQuery } from "../../../api/query/useMessageQueries"

const toNumber = (value) => {
  if (value === null || value === undefined) return 0
  if (typeof value === "number") return Number.isFinite(value) ? value : 0
  const cleaned = String(value).replace(/[^0-9.-]/g, "")
  if (!cleaned) return 0
  const parsed = Number(cleaned)
  return Number.isFinite(parsed) ? parsed : 0
}

const formatCurrency = (value, currency = "₦") =>
  `${currency}${toNumber(value).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`

const formatBookingDate = (value) => {
  if (!value) return "—"
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleDateString("en-GB")
}

const formatBookingTime = (value) => {
  if (!value) return "—"
  if (typeof value === "string") {
    const trimmed = value.trim()
    const timeMatch = trimmed.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/)
    if (timeMatch) {
      const hours = Number(timeMatch[1])
      const minutes = timeMatch[2]
      if (hours >= 0 && hours <= 23) {
        const suffix = hours >= 12 ? "PM" : "AM"
        const normalizedHour = hours % 12 || 12
        return `${normalizedHour}:${minutes}${suffix}`
      }
    }
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return String(value)
  return parsed.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  })
}

const parseDateValue = (value) => {
  if (!value) return null
  const parsed = new Date(value)
  return Number.isNaN(parsed.getTime()) ? null : parsed
}

const getCurrencySymbol = (value) => {
  if (typeof value !== "string") return ""
  const match = value.trim().match(/^[^\d]+/)
  return match ? match[0].trim() : ""
}

const mapBookingStatus = (status) => {
  const normalized = String(status || "").toLowerCase()
  if (["pending_assistant_action", "pending"].includes(normalized)) return "Pending"
  if (
    ["accepted", "assistant_arrived", "trip_started", "confirmed", "completed"].includes(
      normalized
    )
  ) {
    return "Confirmed"
  }
  if (["rejected", "cancelled", "canceled", "failed_payment"].includes(normalized)) {
    return "Canceled"
  }
  return status || "Pending"
}

const normalizeDashboardBooking = (item = {}, index = 0) => {
  const title =
    item?.tour?.title ||
    item?.tour?.name ||
    item?.service?.name ||
    item?.service?.title ||
    item?.experience ||
    item?.title ||
    `Booking #${index + 1}`
  // The API dates a booking by arrivalDate. Without it here the fallback lands
  // on createdAt — when the trip was *booked*, not when it happens — which
  // files trips into the wrong side of Upcoming vs Past.
  const scheduledAt =
    item?.arrivalDate ||
    item?.arrival_date ||
    item?.date ||
    item?.startDate ||
    item?.start_date ||
    item?.scheduledAt ||
    item?.scheduled_at ||
    item?.createdAt ||
    item?.created_at ||
    ""
  // The API prices a booking with amountPaid/amountExpected and a payout
  // breakdown — never a bare `amount`. Reading only the latter valued every
  // trip at zero, which is what emptied Total Earnings.
  const amount = getBookingGrossAmount(item)
  const earnings = getAssistantBookingEarnings(item)
  const participants =
    item?.touristsCount ||
    item?.participants ||
    item?.participantsCount ||
    item?.participants_count ||
    item?.guests ||
    item?.guestCount ||
    item?.guest_count ||
    0

  // A trip occupies every day from arrival to departure, so the calendar needs
  // the raw span, not just the formatted start date.
  const startsAt =
    item?.arrivalDate ||
    item?.arrival_date ||
    item?.startDate ||
    item?.start_date ||
    scheduledAt
  const endsAt =
    item?.departureDate ||
    item?.departure_date ||
    item?.endDate ||
    item?.end_date ||
    startsAt

  return {
    id:
      item?._id ||
      item?.id ||
      item?.bookingId ||
      item?.booking_id ||
      item?.reference ||
      item?.paymentReference ||
      `booking-${index}`,
    title,
    scheduledAt,
    startsAt,
    endsAt,
    dateLabel: formatBookingDate(scheduledAt),
    timeLabel: formatBookingTime(
      item?.time ||
        item?.startTime ||
        item?.start_time ||
        item?.scheduledTime ||
        item?.scheduled_time ||
        scheduledAt
    ),
    participants: toNumber(participants),
    amount,
    earnings,
    rating:
      item?.rating ||
      item?.reviewRating ||
      item?.review_rating ||
      item?.feedback?.rating ||
      null,
    status: mapBookingStatus(item?.status),
    // `status` above folds accepted, started and completed into "Confirmed";
    // Upcoming vs Past needs to know which of those it actually is.
    rawStatus: item?.status,
  }
}

const normalizeBookingsPayload = (payload) => {
  const source = payload?.data ?? payload ?? {}
  const items =
    source?.items ||
    source?.results ||
    source?.bookings ||
    source?.data ||
    source?.rows ||
    []

  return Array.isArray(items)
    ? items.map((item, index) => normalizeDashboardBooking(item, index))
    : []
}

const extractBookingList = (response) => {
  const payload = response?.data || response || {}
  const candidates = [
    payload?.items,
    payload?.results,
    payload?.bookings,
    payload?.data?.items,
    payload?.data?.results,
    payload?.data?.bookings,
    payload?.data,
    response?.items,
    response?.results,
    response?.bookings,
    response,
  ]

  const list = candidates.find((value) => Array.isArray(value))
  return Array.isArray(list) ? list : []
}

const normalizePendingBookingPayload = (payload = {}, fallbackId = "") => {
  const candidate =
    payload?.booking || payload?.data?.booking || payload?.data || payload || {}
  if (!candidate || typeof candidate !== "object") return null

  const guest =
    candidate?.guest ||
    candidate?.traveler ||
    candidate?.user ||
    candidate?.passenger ||
    {}
  const guestName =
    guest?.fullName ||
    [guest?.firstName, guest?.lastName].filter(Boolean).join(" ").trim() ||
    candidate?.guestName ||
    "Guest"

  const arrivalDate =
    formatBookingDate(
      candidate?.arrivalDate ||
        candidate?.startDate ||
        candidate?.checkInDate ||
        candidate?.fromDate ||
        candidate?.arrival_date
    ) || "—"
  const departureDate =
    formatBookingDate(
      candidate?.departureDate ||
        candidate?.endDate ||
        candidate?.checkOutDate ||
        candidate?.toDate ||
        candidate?.departure_date
    ) || "—"

  const priceInput = candidate?.amountExpected ?? candidate?.amountToPay
  const priceNumber =
    typeof priceInput === "number" ? priceInput : toNumber(priceInput)
  const currencySymbol =
    candidate?.currency ||
    (typeof priceInput === "string" ? getCurrencySymbol(priceInput) : "")
  const price =
    priceNumber > 0
      ? formatCurrency(priceNumber, currencySymbol || "₦")
      : candidate?.formattedPrice ||
        candidate?.priceDescription ||
        candidate?.price ||
        "₦0.00"

  const pickup =
    candidate?.pickupLocation ||
    candidate?.pickup ||
    candidate?.pickup_address ||
    candidate?.location ||
    "Pickup location TBD"
  const bookingLocation =
    candidate?.tour?.title ||
    candidate?.tour?.name ||
    candidate?.service?.name ||
    candidate?.service?.title ||
    candidate?.location ||
    pickup
  const note =
    candidate?.itinerary ||
    candidate?.note ||
    candidate?.notes ||
    candidate?.message ||
    candidate?.specialNote ||
    ""

  const id = String(
    candidate?.id || candidate?._id || candidate?.bookingId || fallbackId || ""
  ).trim()

  return {
    id,
    guestName,
    guestAvatar:
      guest?.profilePicture?.url ||
      guest?.profilePicture ||
      guest?.avatar ||
      guest?.image ||
      "/images/testimonial-passport.jpg",
    locationLabel: bookingLocation,
    dateLabel: formatBookingDate(
      candidate?.date ||
        candidate?.startDate ||
        candidate?.scheduledAt ||
        candidate?.scheduled_at ||
        candidate?.arrivalDate ||
        candidate?.arrival_date
    ),
    timeLabel: formatBookingTime(
      candidate?.time ||
        candidate?.startTime ||
        candidate?.start_time ||
        candidate?.scheduledTime ||
        candidate?.scheduled_time ||
        candidate?.scheduledAt ||
        candidate?.scheduled_at
    ),
    arrivalDate,
    departureDate,
    pickup,
    note,
    price,
  }
}

export default function Overview() {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile, expertise, isLoading: isProfileLoading } = useProfileStore()
  const { user: storedUser, isLoading: isAuthLoading } = useAuthStore()
  const toast = useToast()
  const approvalSources = [
    profile,
    expertise,
    storedUser,
  ].filter(Boolean)
  const [bookings, setBookings] = useState([])
  const [bookingsLoading, setBookingsLoading] = useState(false)
  const [pendingBookings, setPendingBookings] = useState([])
  const [pendingLoading, setPendingLoading] = useState(false)
  const [pendingActionTarget, setPendingActionTarget] = useState("")

  const approvalState = useMemo(
    () => resolveApprovalState(...approvalSources),
    [approvalSources]
  )

  const approvalReason = useMemo(
    () => resolveApprovalReason(...approvalSources),
    [approvalSources]
  )

  // The profile/expertise payloads carry no message data, so this has to come
  // from the messages service. The dashboard layout invalidates this same query
  // key on every incoming socket message, which keeps the card live.
  const { data: unreadMessagesData } = useUnreadMessagesCountQuery({ staleTime: 0 })
  const unreadMessagesCount = toNumber(unreadMessagesData?.data?.count)

  // Edits made after first approval live in their own review cycle: the account
  // stays approved, so this is a separate, quieter notice.
  const pendingChanges = profile?.pendingChanges
  const changeReviewState = useMemo(() => {
    if (approvalState !== "approved") return null
    if (pendingChanges?.status === "pending") return "pending"
    if (pendingChanges?.status === "rejected") return "rejected"
    return null
  }, [approvalState, pendingChanges])

  const showPendingSubmissionBanner = useMemo(() => {
    if (approvalState === "approved" || approvalState === "changes_required") {
      return false
    }
    const justSubmitted = Boolean(location.state?.assistantRegistrationSubmitted)
    if (justSubmitted) {
      return true
    }
    const hasLoaded = !isProfileLoading && !isAuthLoading
    return hasLoaded && approvalState === "pending"
  }, [approvalState, location.state, isProfileLoading, isAuthLoading])

  const overviewEmptyState = useMemo(() => {
    if (approvalState === "changes_required") {
      return {
        title: "Approval update needed",
        description:
          "Update the requested details in Settings so your profile can be reviewed and approved.",
      }
    }

    if (showPendingSubmissionBanner) {
      return {
        title: "Approval pending",
        description:
          "Your registration is under super admin review. Trips and bookings will appear here once your account is approved.",
      }
    }

    return {
      title: "No booking yet",
      description: "You have no upcoming trips at the moment.",
    }
  }, [approvalState, showPendingSubmissionBanner])

  const pastTripsEmptyState = useMemo(() => {
    if (approvalState === "changes_required") {
      return {
        title: "Approval update needed",
        description:
          "Update your information in Settings and submit it again for review before completed trips can appear here.",
      }
    }

    if (showPendingSubmissionBanner) {
      return {
        title: "Approval pending",
        description:
          "Your profile is still being reviewed by the super admin. Past trips will appear here after approval and completed bookings.",
      }
    }

    return {
      title: "No booking yet",
      description: "Completed trips will appear here.",
    }
  }, [approvalState, showPendingSubmissionBanner])

  const pendingBookingsEmptyState = useMemo(() => {
    if (approvalState === "changes_required") {
      return {
        title: "Approval update needed",
        description:
          "Go to Settings to make the requested adjustments so you can be reviewed and approved for bookings.",
      }
    }

    if (showPendingSubmissionBanner) {
      return {
        title: "Approval pending",
        description:
          "You will start receiving booking requests after the super admin approves your registration.",
      }
    }

    return {
      title: "No booking yet",
      description: "You have no pending booking requests right now.",
    }
  }, [approvalState, showPendingSubmissionBanner])

  useEffect(() => {
    let active = true
    setBookingsLoading(true)

    bookingService
      .getBookings({ page: 1, limit: 50 })
      .then((response) => {
        if (!active) return
        setBookings(normalizeBookingsPayload(response))
      })
      .catch((error) => {
        if (!active) return
        toast.apiError(error, "Unable to load booking overview right now.")
      })
      .finally(() => {
        if (active) setBookingsLoading(false)
      })

    return () => {
      active = false
    }
  }, [toast])

  useEffect(() => {
    let active = true
    setPendingLoading(true)

    bookingService
      .getPendingAssistantBookings(1, 20)
      .then((response) => {
        if (!active) return
        const list = extractBookingList(response)
        const normalized = list
          .map((item) =>
            normalizePendingBookingPayload(item, item?.id || item?._id || "")
          )
          .filter((item) => item?.id)
        setPendingBookings(normalized)
      })
      .catch((error) => {
        if (!active) return
        toast.apiError(error, "Unable to load pending booking requests.")
      })
      .finally(() => {
        if (active) setPendingLoading(false)
      })

    return () => {
      active = false
    }
  }, [toast])

  const todayStart = useMemo(() => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    return today.getTime()
  }, [])

  const confirmedBookings = useMemo(
    () => bookings.filter((booking) => booking.status === "Confirmed"),
    [bookings]
  )

  // Days the assistant actually has a trip. Pending requests are excluded —
  // they are not booked time until accepted, and the page lists them
  // separately under Pending Bookings.
  const bookedDays = useMemo(
    () => getBookedDayCounts(confirmedBookings),
    [confirmedBookings]
  )

  // A trip is upcoming only until it starts. Once both sides confirm the start
  // it leaves this list even if its arrival date is still ahead — otherwise
  // started and finished trips kept counting as upcoming until the calendar
  // caught up.
  const isUpcomingTrip = (booking) => {
    if (hasTripStarted(booking.rawStatus)) return false
    const bookingDate = parseDateValue(booking.scheduledAt)
    return bookingDate ? bookingDate.getTime() >= todayStart : false
  }

  const upcomingTrips = useMemo(
    () => confirmedBookings.filter(isUpcomingTrip),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [confirmedBookings, todayStart]
  )

  // Everything confirmed that is not upcoming, so a trip that started early
  // moves here instead of dropping out of both lists.
  const pastTrips = useMemo(
    () =>
      confirmedBookings.filter((booking) => {
        if (hasTripStarted(booking.rawStatus)) return true
        const bookingDate = parseDateValue(booking.scheduledAt)
        return bookingDate ? bookingDate.getTime() < todayStart : false
      }),
    [confirmedBookings, todayStart]
  )

  // Her cut of every trip she has been paid for — net of the platform
  // commission, add-ons included. Equals available + ledger balance on her
  // profile once every counted trip is in the wallet.
  const totalEarnings = useMemo(
    () =>
      Math.round(
        confirmedBookings.reduce((sum, booking) => sum + booking.earnings, 0) * 100
      ) / 100,
    [confirmedBookings]
  )

  const statCards = useMemo(
    () => [
      { title: "Total Trips", value: confirmedBookings.length.toLocaleString() },
      { title: "Upcoming Trips", value: upcomingTrips.length.toLocaleString() },
      { title: "Total Earnings", value: formatCurrency(totalEarnings) },
      {
        title: "New Messages",
        value: unreadMessagesCount.toLocaleString(),
      },
    ],
    [confirmedBookings.length, upcomingTrips.length, totalEarnings, unreadMessagesCount]
  )

  const handlePendingAction = async (bookingId, action) => {
    if (!bookingId) return
    setPendingActionTarget(`${bookingId}:${action}`)

    try {
      if (action === "accepted") {
        await bookingService.acceptBooking(bookingId)
      } else {
        await bookingService.rejectBooking(bookingId)
      }

      setPendingBookings((prev) =>
        prev.filter((booking) => booking.id !== bookingId)
      )
      toast.success(
        action === "accepted"
          ? "Booking request accepted."
          : "Booking request rejected."
      )
    } catch (error) {
      toast.apiError(error, "Unable to process this booking request.")
    } finally {
      setPendingActionTarget("")
    }
  }

  return (
    <div className="space-y-6">
      {approvalState === "changes_required" ? (
        <ApprovalBanner
          tone="warning"
          title={approvalReason.title || "Action Required Before Approval"}
          message={
            approvalReason.message ||
            "Your registration could not be approved yet. Go to Settings to make the requested adjustments so your profile can be reviewed again."
          }
          actionLabel="Go to Settings"
          onAction={() => navigate("/dashboards/assistant/settings")}
        />
      ) : null}

      {showPendingSubmissionBanner ? (
        <ApprovalBanner
          tone="success"
          title="Registration Submitted"
          message="Your registration has been submitted successfully and is currently under review by the super admin. You will start receiving trips and bookings once your account is approved."
        />
      ) : null}

      {changeReviewState === "pending" ? (
        <ApprovalBanner
          size="compact"
          tone="info"
          title="Changes under review"
          message="The updates you submitted are being reviewed by the super admin. Your current profile stays live and you keep receiving trips and bookings in the meantime."
        />
      ) : null}

      {changeReviewState === "rejected" ? (
        <ApprovalBanner
          size="compact"
          tone="warning"
          title="Changes not approved"
          message={
            pendingChanges?.adminFeedback ||
            "The updates you submitted were not approved. Review them in Settings and submit again."
          }
          actionLabel="Go to Settings"
          onAction={() => navigate("/dashboards/assistant/settings")}
        />
      ) : null}

      <div className="grid grid-cols-1 gap-4 font-medium sm:grid-cols-2 lg:grid-cols-4">
        {statCards.map((item) => (
          <StatCard key={item.title} title={item.title} value={item.value} />
        ))}
      </div>



      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border bg-white lg:col-span-2">
          <SectionHeader
            title="Upcoming Trips"
            action="View All"
            onAction={() => navigate("/dashboards/assistant/tours")}
          />

          {bookingsLoading ? (
            <SectionMessage message="Loading trips..." />
          ) : upcomingTrips.length > 0 ? (
            <TripsTable
              trips={upcomingTrips.slice(0, 5)}
              columns={["Location", "Participants", "Date", "Time"]}
              variant="upcoming"
            />
          ) : (
            <EmptyState
              title={overviewEmptyState.title}
              description={overviewEmptyState.description}
            />
          )}
        </div>

        <div className="rounded-xl border bg-white p-4">
          <Calendar bookedDays={bookedDays} />
        </div>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-xl border bg-white lg:col-span-2">
          <SectionHeader
            title="Past Trips"
            action="View All"
            onAction={() => navigate("/dashboards/assistant/tours")}
          />

          {bookingsLoading ? (
            <SectionMessage message="Loading trips..." />
          ) : pastTrips.length > 0 ? (
            <TripsTable
              trips={pastTrips.slice(0, 5)}
              columns={["Location", "Participants", "Date", "Rating"]}
              variant="past"
            />
          ) : (
            <EmptyState
              title={pastTripsEmptyState.title}
              description={pastTripsEmptyState.description}
            />
          )}
        </div>

        <div className="rounded-xl border bg-white">
          <SectionHeader
            title="Pending Bookings"
            action="View All"
            onAction={() => navigate("/dashboards/assistant/bookings")}
          />

          <div className="space-y-4 p-4">

            {pendingLoading ? (
              <SectionMessage message="Loading pending bookings..." compact />
            ) : pendingBookings.length > 0 ? (
              pendingBookings.slice(0, 3).map((booking) => (
                <PendingBooking
                  key={booking.id}
                  booking={booking}
                  pendingActionTarget={pendingActionTarget}
                  onAction={handlePendingAction}
                />
              ))
            ) : (
              <EmptyState
                title={pendingBookingsEmptyState.title}
                description={pendingBookingsEmptyState.description}
                compact
              />
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// Amounts are open-ended, so the value shrinks to fit its card rather than
// spilling into the next one — the same approach as zirro's stat card (full
// value, tabular digits, shown in full on hover). It is sized by the card's own
// width (cqi) instead of the viewport, since the sidebar makes viewport width a
// poor guide to how wide a card really is.
function StatCard({ title, value }) {
  return (
    <div className="min-w-0 overflow-hidden rounded-lg border border-[#EEF0F3] bg-white px-4 py-3 [container-type:inline-size]">
      <p className="truncate text-sm font-semibold uppercase tracking-[0.06em] text-gray-500">
        {title}
      </p>
      <p
        className="mt-2 truncate font-semibold leading-[1.2] tabular-nums text-[#101828] text-[clamp(0.875rem,10cqi,1.875rem)]"
        title={String(value)}
      >
        {value}
      </p>
    </div>
  )
}

function ApprovalBanner({
  tone = "warning",
  title,
  message,
  actionLabel,
  onAction,
  // Post-approval notices are informational rather than blocking, so they get a
  // slimmer, left-aligned treatment than the full-width onboarding banner.
  size = "default",
}) {
  const toneClass =
    tone === "success"
      ? "border-green-200 bg-green-50 text-green-800"
      : tone === "info"
        ? "border-[#B9DCF7] bg-[#EFF8FF] text-[#0B4A6F]"
        : "border-[#F4D28D] bg-[#FFF6E5] text-[#B26700]"

  const isCompact = size === "compact"

  return (
    <div
      className={`rounded-xl border ${toneClass} ${
        isCompact ? "px-4 py-3 text-left" : "px-6 py-5 text-center"
      }`}
    >
      <div
        className={
          isCompact
            ? "flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between sm:gap-4"
            : ""
        }
      >
        <div>
          <h2 className={isCompact ? "text-base font-semibold" : "text-2xl font-semibold"}>
            {title}
          </h2>
          <p
            className={
              isCompact
                ? "mt-0.5 text-sm font-medium leading-6"
                : "mx-auto mt-2 max-w-3xl text-base font-medium leading-7"
            }
          >
            {message}
          </p>
        </div>
        {actionLabel ? (
          <button
            type="button"
            onClick={onAction}
            className={`rounded-md bg-white font-semibold text-[#61013B] shadow-sm ring-1 ring-inset ring-[#61013B]/20 ${
              isCompact
                ? "mt-2 shrink-0 px-3 py-1.5 text-sm sm:mt-0"
                : "mt-4 px-4 py-2.5 text-base"
            }`}
          >
            {actionLabel}
          </button>
        ) : null}
      </div>
    </div>
  )
}

function SectionHeader({ title, action, onAction }) {
  return (
    <div className="flex items-center justify-between border-b px-4 py-3">
      <h3 className="text-2xl font-semibold text-[#1D2939]">{title}</h3>
      <button
        type="button"
        className="text-sm font-semibold text-[#61013B]"
        onClick={onAction}
      >
        {action}
      </button>
    </div>
  )
}

function SectionMessage({ message, compact = false }) {
  return (
    <p
      className={`rounded-md border border-dashed px-3 text-base font-medium text-gray-500 ${
        compact ? "py-4" : "m-4 py-6 text-center"
      }`}
    >
      {message}
    </p>
  )
}

function EmptyState({ title, description, compact = false }) {
  return (
    <div
      className={`rounded-md border border-dashed text-center ${
        compact ? "px-3 py-4" : "m-4 px-4 py-8"
      }`}
    >
      <p className="text-2xl font-semibold text-gray-800">{title}</p>
      <p className="mt-2 text-base font-medium leading-7 text-gray-500">{description}</p>
    </div>
  )
}

function TripsTable({ trips, columns, variant }) {
  return (
    <table className="w-full text-sm sm:text-base">
      <thead className="border-b text-left">
        <tr>
          {columns.map((column) => (
            <th
              key={column}
              className="px-2 py-3 text-sm font-semibold uppercase tracking-[0.06em] text-gray-500 sm:px-4"
            >
              {column}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className="divide-y">
        {trips.map((trip) => (
          <tr key={trip.id}>
            <td className="px-2 py-3 text-base font-medium text-[#344054] sm:px-4">
              {trip.title}
            </td>
            <td className="text-base font-medium text-gray-600">
              {trip.participants.toLocaleString()}
            </td>
            <td className="text-base font-medium text-gray-600">{trip.dateLabel}</td>
            {variant === "upcoming" ? (
              <td className="text-base font-medium text-gray-600">{trip.timeLabel}</td>
            ) : (
              <td className="text-base font-medium text-gray-600">
                <RatingCell rating={trip.rating} />
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function RatingCell({ rating }) {
  const ratingValue = Number(rating)
  if (!Number.isFinite(ratingValue) || ratingValue <= 0) {
    return <span>—</span>
  }

  return (
    <span className="inline-flex items-center gap-1 text-base text-[#101828]">
      {ratingValue.toFixed(1)}
      <Star className="h-4 w-4 fill-[#F5B301] text-[#F5B301]" />
    </span>
  )
}

function PendingBooking({ booking, onAction, pendingActionTarget }) {
  return (
    <div className="rounded-lg border border-[#EEF0F3] bg-white p-3">
      <div className="mb-3 flex items-start gap-3">
        <img
          src={booking.guestAvatar}
          alt={booking.guestName}
          className="h-10 w-10 rounded-full object-cover"
        />
        <div className="min-w-0 flex-1 space-y-1">
          <p className="truncate text-base font-semibold text-[#101828]">
            {booking.guestName}
          </p>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-500">
            <span className="inline-flex items-center gap-1">
              <MapPin className="h-3.5 w-3.5" />
              {booking.locationLabel}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock3 className="h-3.5 w-3.5" />
              {booking.timeLabel}
            </span>
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3.5 w-3.5" />
              {booking.dateLabel}
            </span>
          </div>
          {booking.note ? (
            <p className="line-clamp-2 text-sm font-medium leading-5 text-[#7A054A]">
              {booking.note}
            </p>
          ) : null}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onAction?.(booking.id, "accepted")}
          className="flex-1 rounded-md bg-[#61013B] py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-60"
          disabled={Boolean(pendingActionTarget)}
        >
          {pendingActionTarget === `${booking.id}:accepted`
            ? "Accepting..."
            : "Accept"}
        </button>
        <button
          type="button"
          onClick={() => onAction?.(booking.id, "rejected")}
          className="flex-1 rounded-md border border-[#61013B] py-2.5 text-sm font-semibold text-[#61013B] disabled:cursor-not-allowed disabled:opacity-60"
          disabled={Boolean(pendingActionTarget)}
        >
          {pendingActionTarget === `${booking.id}:rejected`
            ? "Declining..."
            : "Decline"}
        </button>
      </div>
    </div>
  )
}

function Calendar({ bookedDays = new Map() }) {
  const days = ["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"]
  const today = useMemo(() => new Date(), [])
  const [currentMonth, setCurrentMonth] = useState(
    new Date(today.getFullYear(), today.getMonth(), 1)
  )
  const [selectedDate, setSelectedDate] = useState(null)

  const monthLabel = currentMonth.toLocaleString("en-US", {
    month: "long",
    year: "numeric",
  })

  const gridDates = useMemo(() => {
    const year = currentMonth.getFullYear()
    const month = currentMonth.getMonth()
    const monthStart = new Date(year, month, 1)
    const monthStartDay = (monthStart.getDay() + 6) % 7
    const gridStart = new Date(year, month, 1 - monthStartDay)
    const daysOut = []

    for (let i = 0; i < 42; i += 1) {
      const date = new Date(gridStart)
      date.setDate(gridStart.getDate() + i)
      daysOut.push(date)
    }

    return daysOut
  }, [currentMonth])

  const goPrevMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
    )
  }

  const goNextMonth = () => {
    setCurrentMonth(
      (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
    )
  }

  const isSameDay = (a, b) =>
    a &&
    b &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <button
          type="button"
          onClick={goPrevMonth}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
          aria-label="Previous month"
        >
          <ChevronLeft />
        </button>
        <h4 className="text-base font-semibold">{monthLabel}</h4>
        <button
          type="button"
          onClick={goNextMonth}
          className="rounded-md p-1 text-gray-500 hover:bg-gray-100"
          aria-label="Next month"
        >
          <ChevronRight />
        </button>
      </div>

      <div className="grid grid-cols-7 gap-2 text-center text-sm">
        {days.map((day) => (
          <div key={day} className="font-semibold text-gray-500">
            {day}
          </div>
        ))}

        {gridDates.map((date) => {
          const inMonth = date.getMonth() === currentMonth.getMonth()
          const isToday = isSameDay(date, today)
          const isSelected = isSameDay(date, selectedDate)
          const tripCount = bookedDays.get(toDayKey(date)) || 0
          const isBooked = tripCount > 0

          const baseClasses =
            "mx-auto flex h-8 w-8 items-center justify-center rounded-full"
          // Booked days stay legible under the today/selected treatments, so
          // the states stack rather than compete.
          const activeClasses = isSelected
            ? "bg-[#61013B] font-semibold text-white"
            : isToday
              ? "bg-[#61013B]/20 font-semibold text-[#61013B]"
              : isBooked
                ? "bg-[#61013B]/10 font-semibold text-[#61013B]"
                : inMonth
                  ? "text-gray-700"
                  : "text-gray-400"

          const label = isBooked
            ? `${date.toLocaleDateString("en-GB")} — ${tripCount} trip${
                tripCount > 1 ? "s" : ""
              }`
            : date.toLocaleDateString("en-GB")

          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => setSelectedDate(date)}
              className="flex flex-col items-center"
              aria-pressed={isSelected}
              aria-label={label}
              title={label}
            >
              <span className={`${baseClasses} ${activeClasses}`}>
                {date.getDate()}
              </span>
              {/* A dot keeps booked days distinguishable even on today or the
                  selected day, where the background is already spoken for. */}
              <span
                className={`mt-0.5 h-1 w-1 rounded-full ${
                  isBooked ? "bg-[#61013B]" : "bg-transparent"
                }`}
              />
            </button>
          )
        })}
      </div>

      {bookedDays.size > 0 ? (
        <div className="mt-3 flex items-center justify-center gap-2 text-xs text-gray-500">
          <span className="h-1.5 w-1.5 rounded-full bg-[#61013B]" />
          <span>Days you have a trip</span>
        </div>
      ) : null}
    </div>
  )
}
